import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { GET_FIELD_DETAILS_EXPERIENCE } from '../../constants';
import { QUERY } from './gql';

export const useFieldDetails = () => {
	const onExperienceSuccess = useExperienceSuccess(GET_FIELD_DETAILS_EXPERIENCE);
	const onExperienceFail = useExperienceFail(GET_FIELD_DETAILS_EXPERIENCE);
	const onExperienceStart = useExperienceStart(GET_FIELD_DETAILS_EXPERIENCE);

	const [getFieldDetailsQuery, { data, loading, error, called }] = useLazyQuery(QUERY, {
		onCompleted: () => {
			onExperienceSuccess();
		},
		onError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'JwmCustomFieldDetails',
					packageName: 'jiraBusinessInlineFieldConfigSidebar',
					teamName: 'deliveroo',
				},
				error:
					Array.isArray(apolloError) && apolloError.length > 0
						? apolloError[0]
						: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(apolloError as ApolloError),
				attributes: { message: 'Failed to retrieve field details' },
				sendToPrivacyUnsafeSplunk: true,
			});
			onExperienceFail('Failed to retrieve field details', apolloError);
		},
		fetchPolicy: 'no-cache',
	});

	const getFieldDetails = useCallback(
		(variables: { fieldId: string; projectId: string; cloudId: string }) => {
			onExperienceStart();
			getFieldDetailsQuery({ variables });
		},
		[onExperienceStart, getFieldDetailsQuery],
	);

	return {
		getFieldDetails,
		fieldDetails: data,
		error,
		loading,
		called,
	};
};
